import React, { useEffect, useState } from "react";
import authApiService from "../../../services/authServices";
import Modal from "../../../utils/modalUtils";
import { sortCountriesForDropdown } from "../../../utils/sortCountriesForDropdown";
import { useNavigate } from "react-router-dom";

/** Get current time in the given timezone as a timestamp (milliseconds). */
const getTimestampInTimeZone = (timezone) => {
    if (!timezone) return null;
    const nowInTZ = new Date(
        new Date().toLocaleString("en-US", { timeZone: timezone })
    );
    return nowInTZ.getTime();
};

const RegisterCity = () => {
    const navigate = useNavigate();
    const [countryTimezones, setCountryTimezones] = useState([]);
    const [countryOptions, setCountryOptions] = useState([]);
    const [timezoneOptions, setTimezoneOptions] = useState([]);
    const [statesForCountry, setStatesForCountry] = useState([]);
    const [isCustomState, setIsCustomState] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        state: "",
        country: "",
        inactive_msg: "",
        countdown_msg: "",
        active: false,
        timezone: "",
    });

    useEffect(() => {
        const fetchCountryTimeZones = async () => {
            try {
                const response = await authApiService.get("getCountryTimeZones");
                if (response && Array.isArray(response.data)) {
                    const rows = response.data;
                    setCountryTimezones(rows);

                    const uniqueCountriesMap = new Map();
                    rows.forEach((row) => {
                        const key = row.countryCode || row.countryName;
                        if (!uniqueCountriesMap.has(key)) {
                            uniqueCountriesMap.set(key, {
                                countryCode: row.countryCode,
                                countryName: row.countryName,
                            });
                        }
                    });
                    setCountryOptions(
                        sortCountriesForDropdown(Array.from(uniqueCountriesMap.values()))
                    );
                } else {
                    setCountryTimezones([]);
                    setCountryOptions([]);
                    setTimezoneOptions([]);
                }
            } catch (error) {
                console.error("Error fetching country timezones", error);
                setCountryTimezones([]);
                setCountryOptions([]);
                setTimezoneOptions([]);
            }
        };

        fetchCountryTimeZones();
    }, []);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleCountryChange = (e) => {
        const { value } = e.target;

        setFormData((prev) => ({
            ...prev,
            country: value,
            state: "",
            timezone: "",
        }));
        setStatesForCountry([]);
        setIsCustomState(false);

        if (!value) {
            setTimezoneOptions([]);
            return;
        }

        const filtered = countryTimezones.filter(
            (row) => row.countryName === value
        );

        const tzOptions = filtered.map((row) => ({
            value: row.timeZone,
            label: `${row.timeZone} (${row.gmtOffset})`,
        }));

        setTimezoneOptions(tzOptions);
    };

    useEffect(() => {
        const fetchStates = async () => {
            if (!formData.country) {
                setStatesForCountry([]);
                return;
            }

            const selected = countryOptions.find((c) => c.countryName === formData.country);
            const countryCode = selected?.countryCode;
            if (!countryCode) return;

            try {
                const response = await authApiService.get("getStatesByCountry", {
                    countryCode,
                });
                if (response && Array.isArray(response.data) && response.data.length > 0) {
                    setStatesForCountry(response.data);
                    setIsCustomState(false);
                } else {
                    setStatesForCountry([]);
                }
            } catch (error) {
                console.error("Error fetching states by country:", error);
                setStatesForCountry([]);
            }
        };

        fetchStates();
    }, [formData.country, countryOptions]);

    const handleStateChange = (e) => {
        const { value } = e.target;

        if (value === "__CUSTOM__") {
            setIsCustomState(true);
            setFormData((prev) => ({ ...prev, state: "" }));
        } else {
            setIsCustomState(false);
            handleChange(e);
        }
    };

    /**
     * Handles form submission for city registration
     * @param {Event} e - The form submit event
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const timezoneTimestamp = getTimestampInTimeZone(formData.timezone);
            const payload = {
                ...formData,
                ...(timezoneTimestamp != null && { timezone_timestamp: timezoneTimestamp }),
            };
            const response = await authApiService.post("city", payload);
            console.log("City registered successfully", response.data);
            Modal.fire({
                icon: "success",
                title: "Success",
                text: "City registered successfully!",
                confirmButtonText: "OK",
                confirmButtonColor: "#addd1e"
            }).then(() => {
                navigate("/carter/citymanage");
            });
        } catch (error) {
            console.error("Error registering city", error);
            Modal.fire({
                icon: "error",
                title: "Error",
                text: error.response?.data?.message || "Error registering city. Please try again.",
                confirmButtonText: "OK",
                confirmButtonColor: "#addd1e"
            });
        }
    };

    return (
        <div id="registerCity">
            <form id="registrationCity" >
                <div className="row">
                    <div className="col-sm-4">
                        <table cellPadding="3" border="0" className="shadow-sm grey-box">
                            <tbody>
                                <tr>
                                    <td><label className="desc">City <span className="req">*</span></label></td>
                                    <td><input name="name" className="text medium" type="text" value={formData.name} onChange={handleChange} required /></td>
                                </tr>
                                <tr>
                                    <td><label className="desc">Country <span className="req">*</span></label></td>
                                    <td>
                                        <select
                                            name="country"
                                            className="text medium select_field"
                                            value={formData.country}
                                            onChange={handleCountryChange}
                                            required
                                        >
                                            <option value="">Select Country</option>
                                            {countryOptions.map((country) => (
                                                <option
                                                    key={country.countryCode || country.countryName}
                                                    value={country.countryName}
                                                >{country.countryName}</option>
                                            ))}
                                        </select>
                                    </td>
                                </tr>
                                <tr>
                                    <td><label className="desc">State <span className="req">*</span></label></td>
                                    <td>
                                        {statesForCountry.length > 0 ? (
                                            <>
                                                <select
                                                    name="state"
                                                    className="text medium select_field"
                                                    value={isCustomState ? "__CUSTOM__" : formData.state}
                                                    onChange={handleStateChange}
                                                    required={!isCustomState}
                                                >
                                                    <option value="">Please Select</option>
                                                    {statesForCountry.map((state) => (
                                                        <option key={state.id} value={state.name}>
                                                            {state.name}
                                                        </option>
                                                    ))}
                                                    <option value="__CUSTOM__">Other / Add new state</option>
                                                </select>
                                                {isCustomState && (
                                                    <input
                                                        name="state"
                                                        className="text medium"
                                                        type="text"
                                                        placeholder="Enter State"
                                                        value={formData.state}
                                                        onChange={handleChange}
                                                        maxLength={50}
                                                        required
                                                        style={{ marginTop: "8px", display: "block" }}
                                                    />
                                                )}
                                            </>
                                        ) : (
                                            <input
                                                name="state"
                                                className="text medium"
                                                type="text"
                                                placeholder={formData.country ? "Enter State" : "Select country first"}
                                                value={formData.state}
                                                onChange={handleChange}
                                                required
                                            />
                                        )}
                                    </td>
                                </tr>
                               
                                <tr>
                                    <td><label className="desc">International Clock <span className="req">*</span></label></td>
                                    <td>
                                        <select
                                            name="timezone"
                                            className="text medium select_field"
                                            value={formData.timezone}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="">Select time zone</option>
                                            {timezoneOptions.map((opt) => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                    </td>
                                </tr>
                                <tr>
                                    <td><label className="desc">Inactive Message</label></td>
                                    <td><input name="inactive_msg" className="text medium" type="text" value={formData.inactive_msg} onChange={handleChange} /></td>
                                </tr>
                                <tr>
                                    <td><label className="desc">CountDown Message</label></td>
                                    <td><input name="countdown_msg" className="text medium" type="text" value={formData.countdown_msg} onChange={handleChange} /></td>
                                </tr>
                                <tr>
                                    <td><label className="desc">Active</label></td>
                                    <td>
                                        <input name="active" type="checkbox" checked={formData.active} onChange={handleChange} />
                                    </td>
                                </tr>
                                <tr>
                                    <td>
                                        <button type="submit" className="btn-green" onClick={handleSubmit}>
                                            Save
                                        </button>
                                    </td>
                                    <td>
                                        <button type="cancle" className="gray_btn"
                                            onClick={() => {
                                                navigate("/carter/citymanage");
                                            }}>
                                            Cancel
                                        </button>
                                    </td>
                            </tr>
                                <tr>
                                    <td colSpan="2" className="text-danger">
                                      * Mandatory Field                                     
                                    </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
        </div>
            </form >
        </div >
    );
};

export default RegisterCity;

